import { useRef, useCallback, useEffect, useState, type ReactNode, type KeyboardEvent } from 'react';
import { Button } from '../Button/Button';
import { SparkIcon } from '../SparkIcon';
import { useControllableState } from '../../hooks/useControllableState';
import './Tabs.css';

export type TabSize = 'large' | 'medium' | 'small';
export type TabPosition = 'top' | 'bottom' | 'left' | 'right';
export type TabVisualState = 'default' | 'hover' | 'disabled';
export type TabsVariant = 'default' | 'card';

export type TabItemData = {
  id: string;
  label: ReactNode;
  disabled?: boolean;
};

/** Single tab button inside a Tabs bar. */
export type TabProps = {
  label: ReactNode;
  active?: boolean;
  size?: TabSize;
  position?: TabPosition;
  disabled?: boolean;
  visualState?: TabVisualState;
  showIcon?: boolean;
  icon?: ReactNode;
  onClick?: () => void;
  className?: string;
};

/** Horizontal tab bar for switching between content panels. Controlled and uncontrolled. */
export type TabsProps = {
  items: TabItemData[];
  /** Controlled active tab id. */
  activeId?: string;
  /** Initial active tab id for uncontrolled mode. */
  defaultActiveId?: string;
  onChange?: (id: string) => void;
  size?: TabSize;
  position?: TabPosition;
  /**
   * Visual style — `'default'` renders underline tabs; `'card'` renders boxed
   * tabs (bordered, rounded top corners, active tab merges with the content
   * below). Card style only supports `position="top"`.
   */
  variant?: TabsVariant;
  showIcon?: boolean;
  /**
   * When true, tabs stretch equally across the full container width
   * (Figma Tabs "Fill container"). Default is hug-content.
   */
  fill?: boolean;
  /**
   * When true (top/bottom positions only), an overflowing tab bar scrolls
   * horizontally behind left/right chevron arrows instead of growing past
   * its container — same mechanics as ValidationCardCarousel. The arrows
   * only render while there is content to scroll to on that side.
   */
  scrollable?: boolean;
  /**
   * Optional node rendered right after the last tab, inside the bar but
   * outside the tab buttons — so it never carries the active underline
   * (e.g. a status tag next to a tab label, Figma Model Configuration).
   * Top/bottom positions only.
   */
  trailing?: ReactNode;
  className?: string;
};

export function Tab({
  label,
  active = false,
  size = 'medium',
  position = 'top',
  disabled = false,
  visualState,
  showIcon = false,
  icon,
  onClick,
  className = '',
}: TabProps) {
  const isDisabled = disabled || visualState === 'disabled';

  const rootClasses = [
    'spark-tab',
    `spark-tab-${size}`,
    `spark-tab-${position}`,
    active ? 'spark-tab-active' : 'spark-tab-inactive',
    isDisabled ? 'spark-tab-disabled' : '',
    visualState ? `spark-tab-state-${visualState}` : '',
    className,
  ]
    .filter(Boolean)
    .join(' ');

  return (
    <button
      type="button"
      role="tab"
      className={rootClasses}
      aria-selected={active}
      disabled={isDisabled}
      onClick={onClick}
      tabIndex={active ? 0 : -1}
    >
      {showIcon ? icon ?? <SparkIcon slug="assembly" size="medium" /> : null}
      <span className="spark-tab-label">{label}</span>
    </button>
  );
}

export function Tabs({
  items,
  activeId: activeIdProp,
  defaultActiveId,
  onChange,
  size = 'medium',
  position = 'top',
  variant = 'default',
  showIcon = false,
  fill = false,
  scrollable = false,
  trailing,
  className = '',
}: TabsProps) {
  const [activeId, setActiveId] = useControllableState(
    activeIdProp,
    defaultActiveId ?? items[0]?.id ?? '',
    onChange,
  );

  const listRef = useRef<HTMLDivElement>(null);

  // Scrollable overflow handling (ValidationCardCarousel mechanics): the tab
  // list is the scroll track; chevrons appear only while that side has
  // overflowed content.
  const isScrollable = scrollable && (position === 'top' || position === 'bottom');
  const [showLeftArrow, setShowLeftArrow] = useState(false);
  const [showRightArrow, setShowRightArrow] = useState(false);

  const checkOverflow = useCallback(() => {
    const el = listRef.current;
    if (!el) return;
    setShowLeftArrow(el.scrollLeft > 0);
    setShowRightArrow(el.scrollLeft + el.clientWidth < el.scrollWidth - 1);
  }, []);

  useEffect(() => {
    if (!isScrollable) return undefined;
    checkOverflow();
    const el = listRef.current;
    if (!el) return undefined;
    el.addEventListener('scroll', checkOverflow, { passive: true });
    const ro = new ResizeObserver(checkOverflow);
    ro.observe(el);
    return () => {
      el.removeEventListener('scroll', checkOverflow);
      ro.disconnect();
    };
    // ``items`` resizes the track's scrollWidth without resizing the track
    // itself (which is all ResizeObserver watches), so re-check on change.
  }, [isScrollable, checkOverflow, items]);

  const scrollTabs = (direction: 'left' | 'right') => {
    const el = listRef.current;
    if (!el) return;
    const step = el.clientWidth * 0.75;
    el.scrollBy({ left: direction === 'left' ? -step : step, behavior: 'smooth' });
  };

  const handleKeyDown = useCallback(
    (e: KeyboardEvent<HTMLDivElement>) => {
      if (e.key !== 'ArrowLeft' && e.key !== 'ArrowRight') return;
      e.preventDefault();

      const enabledItems = items.filter((item) => !item.disabled);
      if (enabledItems.length === 0) return;

      const currentIndex = enabledItems.findIndex((item) => item.id === activeId);
      let nextIndex: number;

      if (e.key === 'ArrowRight') {
        nextIndex = currentIndex >= enabledItems.length - 1 ? 0 : currentIndex + 1;
      } else {
        nextIndex = currentIndex <= 0 ? enabledItems.length - 1 : currentIndex - 1;
      }

      const nextId = enabledItems[nextIndex].id;
      setActiveId(nextId);

      const buttons = listRef.current?.querySelectorAll<HTMLButtonElement>('[role="tab"]');
      const fullIndex = items.findIndex((item) => item.id === nextId);
      buttons?.[fullIndex]?.focus();
    },
    [items, activeId, setActiveId],
  );

  const rootClasses = [
    'spark-tabs',
    `spark-tabs-${size}`,
    `spark-tabs-${position}`,
    variant === 'card' ? 'spark-tabs-card' : '',
    fill ? 'spark-tabs-fill' : '',
    isScrollable ? 'spark-tabs-scrollable' : '',
    className,
  ]
    .filter(Boolean)
    .join(' ');

  const tabList = (
    <div className="spark-tabs-list" role="tablist" ref={listRef} onKeyDown={handleKeyDown}>
      {items.map((item) => (
        <Tab
          key={item.id}
          label={item.label}
          active={item.id === activeId}
          size={size}
          position={position}
          disabled={item.disabled}
          showIcon={showIcon}
          onClick={() => {
            if (!item.disabled) {
              setActiveId(item.id);
            }
          }}
        />
      ))}
    </div>
  );

  const showTrailing = trailing != null && (position === 'top' || position === 'bottom');

  return (
    <div className={rootClasses}>
      {isScrollable ? (
        <div className="spark-tabs-scroll">
          {showLeftArrow && (
            <Button
              variant="ghost"
              size="small"
              type="button"
              className="spark-tabs-scroll-chevron"
              onClick={() => scrollTabs('left')}
              aria-label="Scroll tabs left"
              startIcon={<SparkIcon slug="chevron-left" size="small" />}
            />
          )}
          {tabList}
          {showRightArrow && (
            <Button
              variant="ghost"
              size="small"
              type="button"
              className="spark-tabs-scroll-chevron"
              onClick={() => scrollTabs('right')}
              aria-label="Scroll tabs right"
              startIcon={<SparkIcon slug="chevron-right" size="small" />}
            />
          )}
        </div>
      ) : showTrailing ? (
        <div className="spark-tabs-bar">
          {tabList}
          <div className="spark-tabs-trailing">{trailing}</div>
        </div>
      ) : (
        tabList
      )}
      <div className="spark-tabs-divider" aria-hidden="true" />
    </div>
  );
}
